import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  useRecordContext,
  useEditContext,
  TopToolbar,
  ShowButton,
  DeleteButton,
  Toolbar,
  SaveButton,
  FormDataConsumer,
} from 'react-admin';
import { Box, Typography, Card, CardContent, Chip, Alert } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import WarningIcon from '@mui/icons-material/Warning';
import { partnerMarketingAssetsFields } from '../partnerMarketingAssets.model';

const EditActions = () => (
  <TopToolbar>
    <ShowButton />
    <DeleteButton mutationMode="pessimistic" />
  </TopToolbar>
);

const EditToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const AssetInfoCard = () => {
  const record = useRecordContext();

  if (!record) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Asset Information (Read-Only)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">ID:</Typography>
          <Typography variant="body2">{record.id}</Typography>

          <Typography variant="body2" color="text.secondary">Original Filename:</Typography>
          <Typography variant="body2">{record.original_filename}</Typography>

          <Typography variant="body2" color="text.secondary">File Type:</Typography>
          <Typography variant="body2">{record.file_type}</Typography>

          <Typography variant="body2" color="text.secondary">File Size:</Typography>
          <Typography variant="body2">{formatFileSize(record.file_size_bytes)}</Typography>

          <Typography variant="body2" color="text.secondary">Content Type:</Typography>
          <Typography variant="body2">{record.content_type}</Typography>

          <Typography variant="body2" color="text.secondary">Public URL:</Typography>
          <Box>
            <Chip
              label="View Asset"
              icon={<LaunchIcon />}
              onClick={() => window.open(record.public_url, '_blank')}
              clickable
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const FolderPathWarning = () => {
  const { record } = useEditContext();
  const originalFolderPath = record?.[partnerMarketingAssetsFields.folderPath] || '';

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const currentFolderPath = formData?.[partnerMarketingAssetsFields.folderPath] || '';
        const hasChanged = currentFolderPath !== originalFolderPath;

        if (!hasChanged) return null;

        return (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Warning: Changing the folder path will move the file and change the public URL.
            </Typography>
            <Typography variant="body2">
              The file will be moved in storage, and any existing links to this asset will break.
              Make sure to update any external references (e.g., Webflow) that use the old URL.
            </Typography>
            {originalFolderPath && (
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                Current: {originalFolderPath || '(root)'} → New: {currentFolderPath || '(root)'}
              </Typography>
            )}
          </Alert>
        );
      }}
    </FormDataConsumer>
  );
};

const AssetEditForm = () => {
  return (
    <SimpleForm toolbar={<EditToolbar />}>
      <AssetInfoCard />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Editable Fields
      </Typography>

      <TextInput
        source={partnerMarketingAssetsFields.displayName}
        label="Display Name"
        fullWidth
        helperText="A friendly name for this asset"
      />

      <FolderPathWarning />

      <TextInput
        source={partnerMarketingAssetsFields.folderPath}
        label="Folder Path"
        fullWidth
        helperText="Folder path for organization (e.g., 'partners/acme'). Changing this will move the file and update the public URL."
      />
    </SimpleForm>
  );
};

const AssetEdit = () => {
  return (
    <Edit
      title="Edit Partner Marketing Asset"
      actions={<EditActions />}
      mutationMode="pessimistic"
    >
      <AssetEditForm />
    </Edit>
  );
};

export default AssetEdit;
